import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Drawer, Space, Tag, Tooltip, Typography } from 'antd'
import { DeleteOutlined, DownloadOutlined, FolderOpenOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import type { AudioAssetItem } from '../api'
import { VistoeraActionConfirm } from '../ui/action'
import AudioWaveformPlaceholder from './AudioWaveformPlaceholder'

function formatDurationMs(ms: number | null): string {
  if (ms == null || Number.isNaN(ms) || ms <= 0) return '—'
  const totalSeconds = Math.round(ms / 1000)
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

function formatBytes(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

function formatAbsoluteTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${y}-${mo}-${day} ${hh}:${mm}`
}

/** Audio Detail Drawer — VISTOERA_TENANT_AUDIO_ASSET_CENTER_CANONICAL_
 * UNIFIED_LIBRARY_P0_V1 §56/§57/§58/§59/§60/§62. A right-side Drawer (NOT
 * a modal that takes over the page — task spec §56 explicitly says
 * Drawer, mirroring how `VirtualIpStep2Page`/`PlanComparisonDrawer`
 * already use AntD `Drawer` in this codebase), never a large cover image
 * (task spec §8/§57 — "不使用大封面"). One player element per open
 * Drawer (not the shared single-row-audio-element the list page uses) —
 * opening the Drawer for item B always fully unmounts item A's `<audio>`
 * (native `key={item.id}` remount below), so there is still only ever
 * one actually-playing element at a time system-wide. */
export default function AudioAssetPreviewModal({
  item,
  workspaceId,
  onClose,
  onDelete,
}: {
  item: AudioAssetItem | null
  workspaceId: string
  onClose: () => void
  /** Runs the SAME confirm+delete-API+toast flow as the table row delete
   * action (passed in by the page) — this drawer never re-implements
   * delete semantics of its own. */
  onDelete: (item: AudioAssetItem) => void
}) {
  const { t } = useTranslation(['audioAssets', 'actions'])
  const navigate = useNavigate()
  const [playbackError, setPlaybackError] = useState(false)

  useEffect(() => {
    setPlaybackError(false)
  }, [item?.id])

  if (!item) return null

  const isVoiceTemplate = item.presentation_type === 'VOICE_TEMPLATE'
  const isPlatformRef = item.ownership === 'PLATFORM_REFERENCE'

  const onDownload = () => {
    if (!item.playback_url) return
    window.open(item.playback_url, '_blank', 'noopener')
  }

  const onViewSource = () => {
    if (!item.project_id) return
    navigate(`/workspace/${workspaceId}/content-production/${item.project_id}/canvas`)
  }

  return (
    <Drawer
      open={!!item}
      onClose={onClose}
      width={440}
      title={t('audioAssets:detail.title')}
      data-testid="audio-asset-detail-drawer"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <Typography.Title level={5} style={{ margin: 0 }}>
            {item.name}
          </Typography.Title>
          <Space wrap style={{ marginTop: 8 }}>
            <Tag>{t(`audioAssets:tabs.${item.presentation_type.toLowerCase()}`)}</Tag>
            <Tag color="default">{t(`audioAssets:sources.${item.source}`)}</Tag>
            {isPlatformRef && <Tag color="gold">{t('audioAssets:badges.platformReference')}</Tag>}
            {item.status && (
              <Tag color={item.status === 'FAILED' ? 'error' : item.status === 'SUCCEEDED' ? 'success' : 'processing'}>
                {t(`audioAssets:status.${item.status}`, item.status)}
              </Tag>
            )}
          </Space>
        </div>

        <div>
          {item.has_preview_audio && item.playback_url && !playbackError ? (
            <audio
              key={item.id}
              controls
              src={item.playback_url}
              preload="metadata"
              style={{ width: '100%' }}
              onError={() => setPlaybackError(true)}
              data-testid="audio-asset-detail-player"
            />
          ) : playbackError ? (
            <Typography.Text type="danger">{t('audioAssets:detail.playbackError')}</Typography.Text>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0' }}>
              <AudioWaveformPlaceholder
                variant={isVoiceTemplate ? 'voiceTemplate' : 'audio'}
                width={120}
                height={28}
              />
              <Typography.Text type="secondary">{t('audioAssets:detail.noPreview')}</Typography.Text>
            </div>
          )}
        </div>

        {item.description && (
          <div>
            <div className="aac-detail-label">{t('audioAssets:detail.description')}</div>
            <Typography.Paragraph style={{ margin: 0 }}>{item.description}</Typography.Paragraph>
          </div>
        )}

        {isVoiceTemplate && item.voice_brief && (
          <div>
            <div className="aac-detail-label">{t('audioAssets:detail.voiceBrief')}</div>
            <Typography.Paragraph style={{ margin: 0 }}>{item.voice_brief}</Typography.Paragraph>
          </div>
        )}

        <dl className="aac-detail-grid">
          {!isVoiceTemplate && (
            <div>
              <dt>{t('audioAssets:detail.duration')}</dt>
              <dd>{formatDurationMs(item.duration_ms)}</dd>
            </div>
          )}
          <div>
            <dt>{t('audioAssets:detail.source')}</dt>
            <dd>{t(`audioAssets:sources.${item.source}`)}</dd>
          </div>
          {item.provider && (
            <div>
              <dt>{t('audioAssets:detail.provider')}</dt>
              <dd>{item.provider}</dd>
            </div>
          )}
          <div>
            <dt>{t('audioAssets:detail.createdAt')}</dt>
            <dd>{formatAbsoluteTime(item.created_at)}</dd>
          </div>
          <div>
            <dt>{t('audioAssets:detail.updatedAt')}</dt>
            <dd>{formatAbsoluteTime(item.updated_at)}</dd>
          </div>
          {item.filename && (
            <div>
              <dt>{t('audioAssets:detail.filename')}</dt>
              <dd style={{ wordBreak: 'break-all' }}>{item.filename}</dd>
            </div>
          )}
          {item.size_bytes != null && (
            <div>
              <dt>{t('audioAssets:detail.fileSize')}</dt>
              <dd>{formatBytes(item.size_bytes)}</dd>
            </div>
          )}
          {isVoiceTemplate && (
            <div>
              <dt>{t('audioAssets:detail.referenceAudioCount')}</dt>
              <dd>{item.reference_audio_count ?? '—'}</dd>
            </div>
          )}
          <div>
            <dt>{t('audioAssets:detail.usageCount')}</dt>
            <dd>{item.usage_count ?? '—'}</dd>
          </div>
          <div>
            <dt>{t('audioAssets:detail.tags')}</dt>
            <dd>{item.tags.length > 0 ? item.tags.join(', ') : '—'}</dd>
          </div>
        </dl>

        {isVoiceTemplate && (
          <Typography.Text type={item.usable ? 'success' : 'secondary'}>
            {item.usable ? t('audioAssets:detail.usable') : t('audioAssets:detail.notUsable')}
          </Typography.Text>
        )}

        <Space wrap>
          {item.playback_url && (
            <Button icon={<DownloadOutlined />} onClick={onDownload}>
              {t('audioAssets:actions.download')}
            </Button>
          )}
          {item.project_id && (
            <Button icon={<FolderOpenOutlined />} onClick={onViewSource}>
              {t('audioAssets:actions.viewSource')}
            </Button>
          )}
          {item.can_delete && (
            <VistoeraActionConfirm
              title={t('audioAssets:deleteConfirm.singleTitle')}
              description={t('audioAssets:deleteConfirm.singleDescription')}
              confirmLabel={t('actions:delete')}
              tooltip={t('actions:delete')}
              onConfirm={() => {
                onDelete(item)
              }}
            >
              <Button danger icon={<DeleteOutlined />}>
                {t('audioAssets:actions.delete')}
              </Button>
            </VistoeraActionConfirm>
          )}
          {!item.can_delete && isVoiceTemplate && (
            <Tooltip title={t('audioAssets:toast.voiceTemplateDeleteBlocked')}>
              <Button disabled icon={<DeleteOutlined />}>
                {t('audioAssets:actions.delete')}
              </Button>
            </Tooltip>
          )}
        </Space>
      </div>
    </Drawer>
  )
}
